import { manufacturingFetch } from "@/lib/manufacturingClient";
import { requireSupabase } from "@/lib/supabase";

export type ServiceRequestAttachment = {
  id: string;
  organization_id: string;
  request_id: string;
  storage_bucket: string;
  object_path: string;
  content_type: string;
  byte_size: number;
  filename: string;
  uploaded_by: string | null;
  created_at: string;
};

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  attachment_id: string;
  content_type: string;
  request_id: string;
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.attachment_id === "string"
  );
}

export const SERVICE_REQUEST_PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

export async function uploadServiceRequestAttachment(
  requestId: string,
  file: File
): Promise<{ ok: true; attachment: ServiceRequestAttachment } | { ok: false; error: string }> {
  const sb = requireSupabase();
  const contentType = (file.type || "application/octet-stream").toLowerCase();

  const sign = await manufacturingFetch<unknown>("/api/service/requests/attachments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      request_id: requestId,
      content_type: contentType,
      byte_size: file.size,
      filename: file.name,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) return { ok: false, error: "Unexpected sign response." };

  const { error: upErr } = await sb.storage.from(sign.data.bucket).uploadToSignedUrl(
    sign.data.path,
    sign.data.token,
    file,
    { contentType: sign.data.content_type, upsert: false }
  );
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await manufacturingFetch<{ ok: true; attachment: ServiceRequestAttachment }>(
    "/api/service/requests/attachments",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op: "commit",
        request_id: requestId,
        attachment_id: sign.data.attachment_id,
        object_path: sign.data.path,
        content_type: sign.data.content_type,
        byte_size: file.size,
        filename: file.name,
      }),
    }
  );
  if (!commit.ok) return { ok: false, error: commit.error };
  return { ok: true, attachment: commit.data.attachment };
}

export function listServiceRequestAttachments(requestId: string) {
  const q = new URLSearchParams({ request_id: requestId });
  return manufacturingFetch<{ ok: true; attachments: ServiceRequestAttachment[] }>(
    `/api/service/requests/attachments?${q.toString()}`,
    { method: "GET" }
  );
}
